import { Document, mockDocuments,SharedUser } from "../mocks/documents.mock";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import {
  AlertTriangle,
  ArrowUpDown,
  ArrowUpRight,
  Calendar,
  Download,
  File,
  FileText,
  Filter,
  Folder,
  FolderOpen,
  LayoutGrid,
  List,
  MoreHorizontal,
  Plus,
  Search,
  Share2,
  Star,
  StarOff,
  Tags,
  Upload,
  Users} from "lucide-react";
import { useState } from "react";

// Винесемо утилітарні функції на рівень модуля
const getDocumentIcon = (type: string) => {
  switch (type) {
    case "pdf":
      return <FileText className="h-6 w-6 text-red-500" />;
    case "document":
      return <FileText className="h-6 w-6 text-blue-500" />;
    case "spreadsheet":
      return <FileText className="h-6 w-6 text-green-500" />;
    case "image":
      return <FileText className="h-6 w-6 text-purple-500" />;
    default:
      return <File className="h-6 w-6" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return "bg-emerald-500/15 text-emerald-600";
    case "draft":
      return "bg-amber-500/15 text-amber-600";
    case "archived":
      return "bg-slate-500/15 text-slate-600";
    default:
      return "bg-slate-500/15 text-slate-600";
  }
};

const getLocalizedRole = (role: string) => {
  switch (role) {
    case "owner":
      return "Власник";
    case "editor":
      return "Редактор";
    case "viewer":
      return "Перегляд";
    default:
      return role;
  }
};

const getRoleColor = (role: string) => {
  switch (role) {
    case "owner":
      return "bg-blue-500/15 text-blue-600";
    case "editor":
      return "bg-amber-500/15 text-amber-600";
    case "viewer":
      return "bg-slate-500/15 text-slate-600";
    default:
      return "";
  }
};

const SharedUsersList = ({ users }: { users: SharedUser[] }) => {
  return (
    <div className="space-y-2">
      <div className="text-sm font-medium border-b pb-2 mb-2">
        Користувачі з доступом:
      </div>
      {users.map((user, index) => (
        <div key={index} className="flex items-center justify-between text-sm py-2 hover:bg-accent/50 rounded-md px-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="h-4 w-4 text-primary" />
            </div>
            <div>
              <div className="font-medium">{user.name}</div>
              <div className="text-xs text-muted-foreground">{user.email}</div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant="outline" className={cn(
              "text-xs px-2 py-0.5",
              getRoleColor(user.role)
            )}>
              {getLocalizedRole(user.role)}
            </Badge>
            <div className="text-[10px] text-muted-foreground">
              Надано: {new Date(user.sharedAt).toLocaleDateString('uk-UA')}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

interface DocumentCardProps {
  document: Document;
  onAction: (action: string, doc: Document) => void;
}

const DocumentCard = ({ document, onAction }: DocumentCardProps) => {
  return (
    <Card className="group relative hover:shadow-md transition-all">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            {getDocumentIcon(document.type)}
            <div>
              <CardTitle className="text-sm font-medium group-hover:text-primary transition-colors">
                {document.name}
              </CardTitle>
              <CardDescription className="text-xs">
                {document.folder}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {document.favorite && (
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuLabel>Дії</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onAction("download", document)} className="cursor-pointer">
                  <Download className="mr-2 h-4 w-4" /> Завантажити
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAction("share", document)} className="cursor-pointer">
                  <Share2 className="mr-2 h-4 w-4" /> Поділитися
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAction("favorite", document)} className="cursor-pointer">
                  {document.favorite ? (
                    <>
                      <StarOff className="mr-2 h-4 w-4" /> Прибрати з обраного
                    </>
                  ) : (
                    <>
                      <Star className="mr-2 h-4 w-4" /> Додати до обраного
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onAction("delete", document)}
                  className="text-destructive cursor-pointer"
                >
                  <AlertTriangle className="mr-2 h-4 w-4" /> Видалити
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
          <div className="flex items-center space-x-2">
            <Users className="h-4 w-4" />
            <span>{document.shared.length} користувачів</span>
          </div>
          <Badge variant="secondary" className={cn("text-xs", getStatusColor(document.status))}>
            {document.status === "active" ? "Активний" :
              document.status === "draft" ? "Чернетка" : "Архівний"}
          </Badge>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-2">
            <Calendar className="h-3 w-3" />
            <span>{new Date(document.modified).toLocaleDateString('uk-UA')}</span>
          </div>
          <div>{document.size}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const DocumentsPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState<Document["status"] | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Document;
    direction: 'asc' | 'desc';
  }>({ key: 'modified', direction: 'desc' });
  const [newShareEmail, setNewShareEmail] = useState("");
  const [newShareRole, setNewShareRole] = useState<"editor" | "viewer">("viewer");

  const handleSort = (key: keyof Document) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedAndFilteredDocuments = mockDocuments
    .filter(doc => {
      if (searchQuery) {
        return doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.folder.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return true;
    })
    .filter(doc => {
      if (selectedStatus) {
        return doc.status === selectedStatus;
      }
      return true;
    })
    .sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      switch (sortConfig.key) {
        case 'name':
          return direction * a.name.localeCompare(b.name);
        case 'modified':
          return direction * (new Date(a.modified).getTime() - new Date(b.modified).getTime());
        case 'size':
          return direction * (parseFloat(a.size) - parseFloat(b.size));
        default:
          return 0;
      }
    });

  const handleAction = (action: string, document: Document) => {
    setSelectedDocument(document);
    switch (action) {
      case "share":
        setIsShareDialogOpen(true);
        break;
      case "delete":
        setIsDeleteDialogOpen(true);
        break;
      case "download":
        console.log("Downloading:", document);
        break;
    }
  };

  const handleUpload = (files: FileList | null) => {
    if (files) {
      // Імітація завантаження
      setUploadProgress(0);
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsUploadDialogOpen(false);
            return 100;
          }
          return prev + 10;
        });
      }, 500);
    }
  };

  const handleAddShare = () => {
    console.log("Adding share:", { email: newShareEmail, role: newShareRole });
    setNewShareEmail("");
    setNewShareRole("viewer");
    setIsShareDialogOpen(false);
  };

  return (
    <div className="container mx-auto p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <FileText className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Документи</h1>
            <p className="text-muted-foreground">Управління документами та файлами вашої нерухомості</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => setIsUploadDialogOpen(true)}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Завантажити
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Завантажити нові документи</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button className="shadow-sm">
            <Plus className="mr-2 h-4 w-4" />
            Створити
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="hover:shadow-md transition-all">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Всього документів
            </CardTitle>
            <FileText className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">245</div>
            <div className="flex items-center text-xs text-emerald-600">
              <ArrowUpRight className="mr-1 h-4 w-4" />
              +5 за останній тиждень
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Активні документи
            </CardTitle>
            <File className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">182</div>
            <div className="flex items-center text-xs text-muted-foreground">
              <ArrowUpRight className="mr-1 h-4 w-4 text-emerald-500" />
              +12% цього місяця
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Папки
            </CardTitle>
            <Folder className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">24</div>
            <p className="text-xs text-muted-foreground">
              В 6 папках є нові файли
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Спільний доступ
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">48</div>
            <p className="text-xs text-muted-foreground">
              Документів у спільному доступі
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Command className="rounded-lg border shadow-md">
            <CommandInput
              placeholder="Пошук документів..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
          </Command>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className={cn(showFilters && "bg-accent")}
        >
          <Filter className="mr-2 h-4 w-4" />
          Фільтри
        </Button>
      </div>

      {showFilters && (
        <Card className="p-4">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2">Статус</h3>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("flex-1", selectedStatus === "active" && "bg-accent")}
                  onClick={() => setSelectedStatus(selectedStatus === "active" ? null : "active")}
                >
                  Активні
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("flex-1", selectedStatus === "draft" && "bg-accent")}
                  onClick={() => setSelectedStatus(selectedStatus === "draft" ? null : "draft")}
                >
                  Чернетки
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn("flex-1", selectedStatus === "archived" && "bg-accent")}
                  onClick={() => setSelectedStatus(selectedStatus === "archived" ? null : "archived")}
                >
                  Архівні
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[400px]">Назва</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead>Розмір</TableHead>
              <TableHead>Автор</TableHead>
              <TableHead>Змінено</TableHead>
              <TableHead>Спільний доступ</TableHead>
              <TableHead className="text-right">Дії</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedAndFilteredDocuments.map((document) => (
              <TableRow key={document.id} className="group">
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getDocumentIcon(document.type)}
                    <div>
                      <div className="font-medium">{document.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {document.folder}
                      </div>
                    </div>
                    {document.favorite && (
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className={cn("text-xs", getStatusColor(document.status))}>
                    {document.status === "active" ? "Активний" :
                      document.status === "draft" ? "Чернетка" : "Архівний"}
                  </Badge>
                </TableCell>
                <TableCell>{document.size}</TableCell>
                <TableCell>{document.author}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    {new Date(document.modified).toLocaleDateString('uk-UA')}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {document.shared.length > 0 ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger className="text-sm hover:text-primary transition-colors">
                            {document.shared.length} користувачів
                          </TooltipTrigger>
                          <TooltipContent
                            side="left"
                            sideOffset={40}
                            className="w-[350px] p-4 bg-background border shadow-md rounded-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                          >
                            <div className="space-y-2">
                              <div className="text-sm font-medium border-b pb-2 mb-2 text-foreground">
                                Користувачі з доступом:
                              </div>
                              {document.shared.map((user, index) => (
                                <div key={index} className="flex items-center justify-between text-sm py-2 hover:bg-muted rounded-md px-2">
                                  <div className="flex items-center gap-2">
                                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                                      <Users className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div>
                                      <div className="font-medium text-foreground">{user.name}</div>
                                      <div className="text-xs text-muted-foreground">{user.email}</div>
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-1">
                                    <Badge variant="outline" className={cn(
                                      "text-xs px-2 py-0.5",
                                      getRoleColor(user.role)
                                    )}>
                                      {getLocalizedRole(user.role)}
                                    </Badge>
                                    <div className="text-[10px] text-muted-foreground">
                                      Надано: {new Date(user.sharedAt).toLocaleDateString('uk-UA')}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        Немає спільного доступу
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleAction("download", document)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Завантажити</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleAction("share", document)}
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Спільний доступ</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleAction("delete", document)}
                            className="text-destructive hover:text-destructive"
                          >
                            <AlertTriangle className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Видалити</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Завантаження файлів</DialogTitle>
            <DialogDescription>
              Оберіть файли для завантаження
            </DialogDescription>
          </DialogHeader>
          <div
            className={cn(
              "relative flex h-[200px] flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors",
              "hover:bg-accent hover:border-accent-foreground/50"
            )}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleUpload(e.dataTransfer.files);
            }}
          >
            <Upload className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-2">
              Перетягніть файли сюди або
            </p>
            <label
              htmlFor="file-upload"
              className="text-sm font-medium text-primary hover:underline cursor-pointer"
            >
              оберіть на комп'ютері
              <Input
                id="file-upload"
                type="file"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files)}
                multiple
              />
            </label>
          </div>
          {uploadProgress > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Завантаження...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Спільний доступ</DialogTitle>
            <DialogDescription>
              Налаштування доступу до документу "{selectedDocument?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedDocument && selectedDocument.shared.length > 0 ? (
              <div className="space-y-4">
                <div className="text-sm font-medium">Поточний доступ:</div>
                <SharedUsersList users={selectedDocument.shared} />
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Документ ще не має спільного доступу
              </div>
            )}
            <Separator />
            <div className="space-y-4">
              <div className="text-sm font-medium">Додати користувача:</div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">
                    Email користувача
                  </label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={newShareEmail}
                    onChange={(e) => setNewShareEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">
                    Рівень доступу
                  </label>
                  <Select
                    value={newShareRole}
                    onValueChange={(value: "editor" | "viewer") => setNewShareRole(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Оберіть рівень доступу" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">Редагування</SelectItem>
                      <SelectItem value="viewer">Перегляд</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsShareDialogOpen(false)}>
              Скасувати
            </Button>
            <Button onClick={handleAddShare}>Надати доступ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Видалення документу</AlertDialogTitle>
            <AlertDialogDescription>
              Ви впевнені, що хочете видалити документ "{selectedDocument?.name}"?
              Цю дію неможливо відмінити.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Скасувати</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                console.log("Deleting:", selectedDocument);
                setIsDeleteDialogOpen(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <AlertTriangle className="mr-2 h-4 w-4" />
              Видалити
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DocumentsPage; 